/**
 * Role-based welcome overlay after OAuth login.
 *
 * Integration:
 * - LoginPage: set sessionStorage WELCOME_SESSION_STORAGE_KEY + navigate(..., { state: { showWelcome: true } })
 * - Layout: render <WelcomeOverlayRoot /> once (inside Router)
 */
import React, { useEffect, useRef, useState } from 'react';
import { getFriendlyDisplayName } from '../utils/displayName';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Fade, Grow, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
// import { useBranding } from '../context/BrandingContext';

/** Maps app user.role to welcome copy keys */
export const WELCOME_ROLES = {
  ADMIN: 'ADMIN',

  FACULTY: 'FACULTY',
  STUDENT: 'STUDENT',
  CBC_FACULTY: 'CBC_FACULTY',
};

export const WELCOME_SESSION_STORAGE_KEY = 'stacktrack-welcome-overlay';

export function mapUserRoleToWelcomeRole(userRole) {
  switch (userRole) {
    case 'admin':
      return WELCOME_ROLES.ADMIN;
    case 'faculty':
      return WELCOME_ROLES.FACULTY;
    case 'student':
      return WELCOME_ROLES.STUDENT;
    case 'cbcfaculty':
      return WELCOME_ROLES.CBC_FACULTY;
    default:
      return WELCOME_ROLES.FACULTY;
  }
}

const WELCOME_COPY = {
  [WELCOME_ROLES.ADMIN]: {
    subtitle: 'Manage and monitor your institution',
  },
  [WELCOME_ROLES.STUDENT]: {
    subtitle: 'Track your learning journey',
  },
  [WELCOME_ROLES.FACULTY]: {
    subtitle: 'Manage your courses and assessments',
  },
  [WELCOME_ROLES.CBC_FACULTY]: {
    subtitle: 'Monitor coding performance',
  },
};

const DISPLAY_MS = 2600;
const FADE_MS = 420;

/**
 * Full-screen welcome overlay (presentational).
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {keyof typeof WELCOME_ROLES} props.role
 * @param {string} props.name
 */
export function WelcomeOverlay({ open, role, name, preferredName = null }) {
  const safeName = getFriendlyDisplayName({ fullName: name, preferredName });
  const copy = WELCOME_COPY[role] || WELCOME_COPY[WELCOME_ROLES.FACULTY];
  const { subtitle } = copy;
  // const { displayShortName } = useBranding(); // This is not used in the welcome overlay

  return (
    <Fade in={open} timeout={FADE_MS} unmountOnExit>
      <Box
        role="dialog"
        aria-modal="true"
        aria-labelledby="welcome-overlay-title"
        aria-describedby="welcome-overlay-greeting welcome-overlay-tagline"
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (t) => t.zIndex.modal + 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.88),
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        <Grow in={open} timeout={FADE_MS} style={{ transformOrigin: 'center center' }}>
          <Box
            sx={{
              textAlign: 'center',
              maxWidth: 520,
            }}
          >
            <Typography
              id="welcome-overlay-title"
              variant="h4"
              component="h1"
              fontWeight={700}
              sx={{
                color: 'text.primary',
                letterSpacing: '-0.03em',
                mb: 1.25,
                fontSize: { xs: '1.5rem', sm: '1.75rem' },
              }}
            >
              Welcome to StackTrack
            </Typography>
            <Typography
              id="welcome-overlay-greeting"
              variant="body1"
              component="p"
              color="text.secondary"
              sx={{
                mb: 1.5,
                fontWeight: 500,
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                letterSpacing: '0.01em',
              }}
            >
              Hello, {safeName}
            </Typography>
            <Typography
              id="welcome-overlay-tagline"
              variant="body2"
              component="p"
              color="text.secondary"
              sx={{
                opacity: 0.9,
                lineHeight: 1.5,
                maxWidth: 400,
                mx: 'auto',
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Grow>
      </Box>
    </Fade>
  );
}

/**
 * Root integration: shows welcome once after OAuth login (sessionStorage flag + optional router state).
 * Mount once inside Layout (inside Router).
 */
export function WelcomeOverlayRoot() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const welcomeConsumedRef = useRef(false);
  const dismissTimerRef = useRef(null);

  const userKey =
    user?._id != null ? String(user._id) : user?.id != null ? String(user.id) : user?.email ?? '';

  useEffect(() => {
    if (!userKey) {
      welcomeConsumedRef.current = false;
      if (dismissTimerRef.current != null) {
        window.clearTimeout(dismissTimerRef.current);
        dismissTimerRef.current = null;
      }
      setOpen(false);
      return;
    }

    let fromSession = false;
    try {
      fromSession = sessionStorage.getItem(WELCOME_SESSION_STORAGE_KEY) === '1';
    } catch {
      /* ignore */
    }
    const fromRouter = Boolean(location.state?.showWelcome);

    if (!fromSession && !fromRouter) {
      if (!welcomeConsumedRef.current) {
        setOpen(false);
      }
      return;
    }

    if (welcomeConsumedRef.current) {
      return;
    }
    welcomeConsumedRef.current = true;

    try {
      if (fromSession) sessionStorage.removeItem(WELCOME_SESSION_STORAGE_KEY);
    } catch {
      /* ignore */
    }

    if (fromRouter) {
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: {} },
      );
    }

    setOpen(true);
    if (dismissTimerRef.current != null) {
      window.clearTimeout(dismissTimerRef.current);
    }
    dismissTimerRef.current = window.setTimeout(() => {
      setOpen(false);
      dismissTimerRef.current = null;
    }, DISPLAY_MS);

    return () => {
      /* Do not clear dismissTimerRef here: React Strict Mode re-runs this effect after clearing
         session / router flags; clearing the timeout would leave `open` stuck true. */
    };
    // Stable identity: full `user` object from Zustand may change reference without a new login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  if (!user) return null;

  const role = mapUserRoleToWelcomeRole(user.role);

  return <WelcomeOverlay open={open} role={role} name={user.name} preferredName={user.preferredName} />;
}
